import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import CreditPurchaseModal from '../components/credits/CreditPurchaseModal';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useHasPermission } from '../hooks/usePermission';
import { fetchBalance, fetchTransactionHistory } from '../redux/slices/creditSlice';
import ForbiddenPage from './ForbiddenPage';

/**
 * CreditHistoryPage
 * Page for viewing credit transaction history
 * Phase 7: Credit System
 * 
 * Route: /credits/history
 * Permission: credits.view
 */
const CreditHistoryPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const hasPermission = useHasPermission('credits.view');
  const hasPurchasePermission = useHasPermission('credits.purchase');

  const { balance, transactions, pagination, loading } = useSelector(state => state.credits);

  const [filters, setFilters] = useState({
    type: '',
    startDate: '',
    endDate: ''
  });
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (hasPermission) {
      dispatch(fetchBalance());
    }
  }, [dispatch, hasPermission]);

  useEffect(() => {
    if (hasPermission) {
      const params = {
        ...filters,
        page,
        limit: 20
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      dispatch(fetchTransactionHistory(params));
    }
  }, [dispatch, hasPermission, filters, page]);

  const handleBack = () => {
    history.goBack();
  };

  const handleFilterChange = (field) => (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when filter changes
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const getTypeLabel = (type) => {
    const labels = {
      purchase: 'Satın Alma',
      usage: 'Kullanım',
      manual_add: 'Manuel Yükleme',
      manual_subtract: 'Manuel Çıkarma',
      refund: 'İade'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      purchase: 'success',
      usage: 'error',
      manual_add: 'info',
      manual_subtract: 'warning',
      refund: 'default'
    };
    return colors[type] || 'default';
  };

  const formatAmount = (amount) => {
    return amount.toFixed(2);
  };

  // Permission check
  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Geri Dön
      </Button>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Kredi İşlem Geçmişi
        </Typography>
        {hasPurchasePermission && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
          >
            Kredi Yükle
          </Button>
        )}
      </Box>

      {/* Current Balance Card */}
      <Card sx={{ mb: 4, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <CardContent>
          <Typography variant="h4" align="center">
            Mevcut Bakiye: {balance} Kredi
          </Typography>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>İşlem Tipi</InputLabel>
              <Select
                value={filters.type}
                onChange={handleFilterChange('type')}
                label="İşlem Tipi"
              >
                <MenuItem value="">Tümü</MenuItem>
                <MenuItem value="purchase">Satın Alma</MenuItem>
                <MenuItem value="usage">Kullanım</MenuItem>
                <MenuItem value="manual_add">Manuel Yükleme</MenuItem>
                <MenuItem value="manual_subtract">Manuel Çıkarma</MenuItem>
                <MenuItem value="refund">İade</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Başlangıç Tarihi"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange('startDate')}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              label="Bitiş Tarihi"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange('endDate')}
              size="small"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && transactions.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Transactions Table */}
      {!loading && transactions.length > 0 && (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Tarih</strong></TableCell>
                  <TableCell><strong>İşlem Tipi</strong></TableCell>
                  <TableCell><strong>Miktar</strong></TableCell>
                  <TableCell><strong>Bakiye Öncesi</strong></TableCell>
                  <TableCell><strong>Bakiye Sonrası</strong></TableCell>
                  <TableCell><strong>İlişkili Doküman</strong></TableCell>
                  <TableCell><strong>İlişkili Şablon</strong></TableCell>
                  <TableCell><strong>Açıklama</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction._id} hover>
                    <TableCell>
                      {format(new Date(transaction.createdAt), 'dd MMM yyyy HH:mm', { locale: tr })}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getTypeLabel(transaction.type)}
                        color={getTypeColor(transaction.type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={transaction.type === 'usage' || transaction.type === 'manual_subtract' ? 'error.main' : 'success.main'}
                        fontWeight="bold"
                      >
                        {transaction.type === 'usage' || transaction.type === 'manual_subtract' ? '-' : '+'}
                        {formatAmount(transaction.amount)} Kredi
                      </Typography>
                    </TableCell>
                    <TableCell>{formatAmount(transaction.balanceBefore)} Kredi</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatAmount(transaction.balanceAfter)} Kredi
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {transaction.documentId ? (
                        <Button
                          size="small"
                          onClick={() => history.push(`/documents/${transaction.documentId._id || transaction.documentId}`)}
                        >
                          {transaction.documentId.title || 'Doküman'}
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {transaction.templateId ? (
                        <Button
                          size="small"
                          onClick={() => history.push(`/templates/${transaction.templateId._id || transaction.templateId}`)}
                        >
                          {transaction.templateId.name || transaction.templateId.title || 'Şablon'}
                        </Button>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {transaction.description || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination.total > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={Math.ceil(pagination.total / pagination.limit)}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      {/* No Transactions Message */}
      {!loading && transactions.length === 0 && (
        <Alert severity="info">
          Henüz işlem geçmişi bulunmamaktadır.
        </Alert>
      )}

      {/* Purchase Modal */}
      <CreditPurchaseModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => {
          dispatch(fetchBalance());
          setModalOpen(false);
        }}
      />
    </Container>
  );
};

export default CreditHistoryPage;

